import { formatCurrency } from '@/modules/shared/utils/formatCurrency';

/**
 * Format utilities for workspace display values.
 * Handles null/empty/invalid values by returning "-" fallback.
 */

/**
 * Check if a date string is valid (not epoch, not null, not empty).
 */
function isValidDate(dateString: string | null | undefined): boolean {
  if (!dateString) return false;
  
  const date = new Date(dateString);
  
  // Check if date is invalid
  if (isNaN(date.getTime())) return false;
  
  // Check for epoch dates (1970-01-01)
  const epochStart = new Date('1970-01-01T00:00:00Z').getTime();
  const epochEnd = new Date('1970-01-02T00:00:00Z').getTime();
  if (date.getTime() >= epochStart && date.getTime() < epochEnd) return false;
  
  // Check for unreasonably old dates (before 1900 — covers all plausible DOB values)
  const minYear = 1900;
  if (date.getFullYear() < minYear) return false;
  
  return true;
}

/**
 * Format a date string for display.
 * Returns "-" for null, empty, invalid, or epoch dates.
 * 
 * @param dateString - The date string to format
 * @param options - Intl.DateTimeFormat options (defaults to "Jan 1, 2024" format)
 * @returns Formatted date string or "-"
 */
export function formatDisplayDate(
  dateString: string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }
): string {
  if (!isValidDate(dateString)) return '-';
  
  try {
    return new Date(dateString!).toLocaleDateString('en-US', options);
  } catch {
    return '-';
  }
}

/**
 * Format a date string with time.
 * Returns "-" for null, empty, invalid, or epoch dates.
 * 
 * @param dateString - The date string to format
 * @returns Formatted datetime string or "-"
 */
export function formatDisplayDateTime(dateString: string | null | undefined): string {
  if (!isValidDate(dateString)) return '-';
  
  try {
    return new Date(dateString!).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
}

/**
 * Format a relative time string (e.g., "2h ago", "3d ago").
 * Returns "-" for null, empty, invalid, or epoch dates.
 * 
 * @param dateString - The date string to format
 * @returns Relative time string or "-"
 */
export function formatRelativeTime(dateString: string | null | undefined): string {
  if (!isValidDate(dateString)) return '-';
  
  try {
    const date = new Date(dateString!);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
}

/**
 * Format any display value, returning "-" for null/undefined/empty.
 * 
 * @param value - The value to format
 * @returns String representation or "-"
 */
export function formatDisplayValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string' && value.trim() === '') return '-';
  if (typeof value === 'number' && isNaN(value)) return '-';
  
  return String(value);
}

/**
 * Format currency value.
 * Returns "-" for null/undefined/NaN.
 * 
 * @param amount - The amount to format
 * @param currency - Currency code (default: USD)
 * @returns Formatted currency string or "-"
 */
export function formatDisplayCurrency(
  amount: number | null | undefined,
  currency: string = 'USD'
): string {
  return formatCurrency(amount, currency);
}

/** RFC 4122 GUID pattern used for raw-ID detection. */
const GUID_DETECT = /^[{]?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}[}]?$/i;

/**
 * Safe display: returns the value as-is, UNLESS it looks like a raw GUID/ID,
 * in which case it returns the provided fallback.
 *
 * Use this anywhere a string might inadvertently contain a foreign-key ID.
 *
 * @example
 * formatSafeDisplayValue(patient.hospitalId)          // → "Not available"
 * formatSafeDisplayValue(patient.hospitalName)        // → "City Care Hospital"
 * formatSafeDisplayValue(patient.hospitalName, "—")   // → "—" when empty
 */
export function formatSafeDisplayValue(
  value: string | null | undefined,
  fallback = 'Not available',
): string {
  if (!value || value.trim() === '') return fallback;
  if (GUID_DETECT.test(value.trim())) {
    if (import.meta.env.DEV) {
      console.warn('[UI Guard] Raw GUID suppressed from display:', value);
    }
    return fallback;
  }
  return value;
}
